import React, { useState, useEffect } from 'react';
import axios from 'axios';

// API URL - usar caminho relativo
const API = '/api';

// Função para obter headers com autenticação
const getAuthHeaders = () => {
  const token = localStorage.getItem('ebd_token');
  return token ? {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  } : {
    'Content-Type': 'application/json'
  };
};

const ConfigDepartamentos = ({ setCurrentView, currentUser }) => {
  const [config, setConfig] = useState({
    departamentos: {},
    turmas_excluidas: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Estado para lista de todas as turmas cadastradas no sistema
  const [turmasCadastradas, setTurmasCadastradas] = useState([]);

  // Estados para adicionar novo departamento
  const [novoDepartamento, setNovoDepartamento] = useState('');
  const [showAddDept, setShowAddDept] = useState(false);

  // Estados para adicionar turma a departamento
  const [novaTurma, setNovaTurma] = useState({});
  const [showAddTurma, setShowAddTurma] = useState({});

  // Estado para adicionar turma excluída
  const [novaTurmaExcluida, setNovaTurmaExcluida] = useState('');
  const [showAddExcluida, setShowAddExcluida] = useState(false);

  useEffect(() => {
    loadConfig();
    loadTurmas();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/departamentos-config`, { headers: getAuthHeaders() });
      setConfig(response.data);
    } catch (error) {
      console.error('Erro ao carregar configuração:', error);
      setMessage({ type: 'error', text: 'Erro ao carregar configuração de departamentos' });
    } finally {
      setLoading(false);
    }
  };

  const loadTurmas = async () => {
    try {
      const response = await axios.get(`${API}/turmas`, { headers: getAuthHeaders() });
      // Filtrar apenas turmas ativas e ordenar por nome
      const turmasAtivas = response.data
        .filter(t => !t.desativada)
        .sort((a, b) => a.nome.localeCompare(b.nome));
      setTurmasCadastradas(turmasAtivas);
    } catch (error) {
      console.error('Erro ao carregar turmas:', error);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });

      await axios.put(`${API}/departamentos-config`, {
        departamentos: config.departamentos,
        turmas_excluidas: config.turmas_excluidas,
        user_id: currentUser?.id || 'unknown'
      }, { headers: getAuthHeaders() });

      setMessage({ type: 'success', text: 'Configuração salva com sucesso!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Erro ao salvar configuração:', error);
      setMessage({ type: 'error', text: 'Erro ao salvar configuração' });
    } finally {
      setSaving(false);
    }
  };

  const adicionarDepartamento = () => {
    if (!novoDepartamento.trim()) {
      alert('Digite um nome para o departamento');
      return;
    }

    if (config.departamentos[novoDepartamento]) {
      alert('Departamento já existe');
      return;
    }

    setConfig({
      ...config,
      departamentos: {
        ...config.departamentos,
        [novoDepartamento]: []
      }
    });
    setNovoDepartamento('');
    setShowAddDept(false);
  };

  const removerDepartamento = (nomeDept) => {
    if (!window.confirm(`Tem certeza que deseja remover o departamento "${nomeDept}"?`)) {
      return;
    }

    const novosDepartamentos = { ...config.departamentos };
    delete novosDepartamentos[nomeDept];
    setConfig({
      ...config,
      departamentos: novosDepartamentos
    });
  };

  // Função para obter turmas disponíveis para um departamento específico
  const getTurmasDisponiveis = (nomeDept) => {
    // Turmas já adicionadas em TODOS os departamentos
    const turmasEmDepartamentos = Object.values(config.departamentos).flat();

    // Retornar turmas que não estão em nenhum departamento e não estão excluídas
    return turmasCadastradas.filter(turma =>
      !turmasEmDepartamentos.includes(turma.nome) &&
      !config.turmas_excluidas.includes(turma.nome)
    );
  };

  // Função para obter turmas disponíveis para exclusão
  const getTurmasDisponiveisExclusao = () => {
    // Turmas já adicionadas em TODOS os departamentos
    const turmasEmDepartamentos = Object.values(config.departamentos).flat();

    // Retornar turmas que não estão na lista de excluídas
    return turmasCadastradas.filter(turma =>
      !config.turmas_excluidas.includes(turma.nome) &&
      !turmasEmDepartamentos.includes(turma.nome)
    );
  };

  const adicionarTurma = (nomeDept) => {
    const turma = novaTurma[nomeDept];
    if (!turma) {
      alert('Selecione uma turma');
      return;
    }

    if (config.departamentos[nomeDept].includes(turma)) {
      alert('Turma já existe neste departamento');
      return;
    }

    setConfig({
      ...config,
      departamentos: {
        ...config.departamentos,
        [nomeDept]: [...config.departamentos[nomeDept], turma]
      }
    });
    setNovaTurma({ ...novaTurma, [nomeDept]: '' });
    setShowAddTurma({ ...showAddTurma, [nomeDept]: false });
  };

  const removerTurma = (nomeDept, turma) => {
    setConfig({
      ...config,
      departamentos: {
        ...config.departamentos,
        [nomeDept]: config.departamentos[nomeDept].filter(t => t !== turma)
      }
    });
  };

  const adicionarTurmaExcluida = () => {
    if (!novaTurmaExcluida) {
      alert('Selecione uma turma');
      return;
    }

    if (config.turmas_excluidas.includes(novaTurmaExcluida)) {
      alert('Turma já está na lista de excluídas');
      return;
    }

    setConfig({
      ...config,
      turmas_excluidas: [...config.turmas_excluidas, novaTurmaExcluida]
    });
    setNovaTurmaExcluida('');
    setShowAddExcluida(false);
  };

  const removerTurmaExcluida = (turma) => {
    setConfig({
      ...config,
      turmas_excluidas: config.turmas_excluidas.filter(t => t !== turma)
    });
  };

  const getCoresDepartamento = (index) => {
    const cores = [
      { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-800', btn: 'bg-green-500 hover:bg-green-600' },
      { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-800', btn: 'bg-blue-500 hover:bg-blue-600' },
      { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-800', btn: 'bg-purple-500 hover:bg-purple-600' },
      { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-800', btn: 'bg-orange-500 hover:bg-orange-600' },
      { bg: 'bg-pink-50', border: 'border-pink-200', text: 'text-pink-800', btn: 'bg-pink-500 hover:bg-pink-600' },
    ];
    return cores[index % cores.length];
  };

  if (loading) {
    return (
      <div className="p-4 md:p-6 bg-gray-50 min-h-screen">
        <div className="max-w-6xl mx-auto">
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">Carregando configurações...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 bg-gray-50 min-h-screen">
      <div className="max-w-6xl mx-auto">
        <button
          onClick={() => setCurrentView('dashboard')}
          className="mb-4 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
        >
          ← Voltar ao Dashboard
        </button>

        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Configurar Departamentos</h1>
            <p className="text-gray-600">Configure os departamentos e classes para os relatórios de vencedores</p>
          </div>

          {/* Mensagem de feedback */}
          {message.text && (
            <div className={`mb-6 p-4 rounded-lg ${message.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
              {message.text}
            </div>
          )}

          {/* Departamentos */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-semibold text-gray-800">Departamentos e Turmas</h2>
              <button
                onClick={() => setShowAddDept(!showAddDept)}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                + Novo Departamento
              </button>
            </div>

            {/* Form para adicionar departamento */}
            {showAddDept && (
              <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <label className="block text-sm font-medium text-gray-700 mb-2">Nome do Departamento</label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={novoDepartamento}
                    onChange={(e) => setNovoDepartamento(e.target.value)}
                    placeholder="Ex: Melhor Idade"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    onKeyPress={(e) => e.key === 'Enter' && adicionarDepartamento()}
                  />
                  <button
                    onClick={adicionarDepartamento}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                  >
                    Adicionar
                  </button>
                  <button
                    onClick={() => setShowAddDept(false)}
                    className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            )}

            {/* Lista de departamentos */}
            <div className="space-y-4">
              {Object.keys(config.departamentos).map((nomeDept, index) => {
                const cores = getCoresDepartamento(index);
                return (
                  <div key={nomeDept} className={`${cores.bg} border ${cores.border} rounded-lg p-4`}>
                    <div className="flex justify-between items-center mb-3">
                      <h3 className={`text-lg font-semibold ${cores.text}`}>📁 {nomeDept}</h3>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => setShowAddTurma({ ...showAddTurma, [nomeDept]: !showAddTurma[nomeDept] })}
                          className={`px-3 py-1 ${cores.btn} text-white text-sm rounded hover:opacity-90`}
                        >
                          + Turma
                        </button>
                        <button
                          onClick={() => removerDepartamento(nomeDept)}
                          className="px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600"
                        >
                          🗑️ Remover
                        </button>
                      </div>
                    </div>

                    {/* Form para adicionar turma */}
                    {showAddTurma[nomeDept] && (
                      <div className="mb-3 p-3 bg-white rounded border border-gray-200">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Selecione a Turma</label>
                        <div className="flex space-x-2">
                          <select
                            value={novaTurma[nomeDept] || ''}
                            onChange={(e) => setNovaTurma({ ...novaTurma, [nomeDept]: e.target.value })}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">-- Selecione uma turma --</option>
                            {getTurmasDisponiveis(nomeDept).map(turma => (
                              <option key={turma.nome} value={turma.nome}>
                                {turma.nome}
                              </option>
                            ))}
                          </select>
                          <button
                            onClick={() => adicionarTurma(nomeDept)}
                            className="px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                          >
                            Adicionar
                          </button>
                          <button
                            onClick={() => setShowAddTurma({ ...showAddTurma, [nomeDept]: false })}
                            className="px-3 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                          >
                            Cancelar
                          </button>
                        </div>
                        {getTurmasDisponiveis(nomeDept).length === 0 && (
                          <p className="text-sm text-orange-600 mt-2">
                            ⚠️ Todas as turmas já foram adicionadas ou não há turmas cadastradas no sistema.
                          </p>
                        )}
                      </div>
                    )}

                    {/* Lista de turmas */}
                    <div className="space-y-2">
                      {config.departamentos[nomeDept].length === 0 ? (
                        <p className="text-gray-500 text-sm italic">Nenhuma turma adicionada</p>
                      ) : (
                        config.departamentos[nomeDept].map((turma, idx) => (
                          <div key={idx} className="flex justify-between items-center bg-white rounded px-3 py-2 border border-gray-200">
                            <span className="text-gray-700">├─ {turma}</span>
                            <button
                              onClick={() => removerTurma(nomeDept, turma)}
                              className="text-red-500 hover:text-red-700 text-sm"
                            >
                              ✕
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                );
              })}

              {Object.keys(config.departamentos).length === 0 && (
                <p className="text-gray-500 italic text-center py-8">Nenhum departamento configurado. Clique em "Novo Departamento" para começar.</p>
              )}
            </div>
          </div>

          {/* Turmas Excluídas */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-semibold text-gray-800">Turmas Excluídas dos Cálculos</h2>
              <button
                onClick={() => setShowAddExcluida(!showAddExcluida)}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                + Adicionar Exclusão
              </button>
            </div>

            {/* Form para adicionar turma excluída */}
            {showAddExcluida && (
              <div className="mb-4 p-4 bg-red-50 rounded-lg border border-red-200">
                <label className="block text-sm font-medium text-gray-700 mb-2">Selecione a Turma para Excluir</label>
                <div className="flex space-x-2">
                  <select
                    value={novaTurmaExcluida}
                    onChange={(e) => setNovaTurmaExcluida(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    <option value="">-- Selecione uma turma --</option>
                    {getTurmasDisponiveisExclusao().map(turma => (
                      <option key={turma.nome} value={turma.nome}>
                        {turma.nome}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={adicionarTurmaExcluida}
                    className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                  >
                    Adicionar
                  </button>
                  <button
                    onClick={() => setShowAddExcluida(false)}
                    className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
                  >
                    Cancelar
                  </button>
                </div>
                {getTurmasDisponiveisExclusao().length === 0 && (
                  <p className="text-sm text-orange-600 mt-2">
                    ⚠️ Todas as turmas já foram adicionadas ou excluídas, ou não há turmas cadastradas no sistema.
                  </p>
                )}
              </div>
            )}

            {/* Lista de turmas excluídas */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              {config.turmas_excluidas.length === 0 ? (
                <p className="text-gray-500 text-sm italic">Nenhuma turma excluída</p>
              ) : (
                <div className="space-y-2">
                  {config.turmas_excluidas.map((turma, idx) => (
                    <div key={idx} className="flex justify-between items-center bg-white rounded px-3 py-2 border border-red-300">
                      <span className="text-gray-700">🚫 {turma}</span>
                      <button
                        onClick={() => removerTurmaExcluida(turma)}
                        className="text-red-500 hover:text-red-700 text-sm"
                      >
                        ✕ Remover
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Informações */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-blue-800 mb-2">ℹ️ Como funciona:</h3>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• <strong>Departamentos:</strong> Grupos que competem entre si (ex: Infantil, Jovens, Adulto)</li>
              <li>• <strong>Turmas:</strong> Classes que pertencem a cada departamento</li>
              <li>• <strong>Classes Vencedoras:</strong> Calculadas automaticamente por departamento com base em frequência e oferta</li>
              <li>• <strong>Turmas Excluídas:</strong> Não participam dos cálculos de vencedores (ex: Professores, Total Geral)</li>
              <li>• <strong>Relatórios:</strong> Mostram os vencedores de cada departamento automaticamente</li>
            </ul>
          </div>

          {/* Botões de ação */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              onClick={() => setCurrentView('dashboard')}
              className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {saving ? 'Salvando...' : 'Salvar Configurações'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfigDepartamentos;
